<template>
  <fieldset id="trims-wp" class="tla-form-group tla-form-group--margin-medium" v-if="showTrims">
    <label ref="trimLabelElement" class="tla-form-group__label tla-form-group__label--required"
      v-html="trimText"></label>

    <div class="tla-form-group__touch-points">
      <touchpoint v-for="(item) in trimOptions"
      :key="`${item.touchpointValue}-${touchpointKey}`"
      :collectionName="'trimOptions'"
      :inputId="`${item.touchpointValue}-${touchpointKey}`"
      :inputValue="item.touchpointValue"
      :isSelected="item.preSelect || item.touchpointName == trimSelected"
      :validationScope="validationScope"
      :validationRules="''"
      @trimOptions="submitTrim($event)">
      <touchpointText slot="touchpoint" :content="item.touchpointName"></touchpointText>
      </touchpoint>

      <touchpoint v-if="allowNotSure"
      :collectionName="'trimOptions'"
      :inputId="`NotSure-${touchpointKey}`"
      :inputValue="'Not Sure'"
      :isSelected="trimNotSure"
      :isFullWidth="true"
      :validationScope="validationScope"
      :validationRules="''"
      @trimOptions="submitExtraData()">
      <div class="generic-touchpoint__text" slot="touchpoint" v-html="'Not Sure'"></div>
      </touchpoint>
    </div>
    <span class="tla-form-group__message " :class="getTrimValidationClass('message')">
      Please select an option.
    </span>
    <vehicleTrimBot
    v-if="allowTrimBot"
    ref="vehicleTrimBot"
    @trimSelected="submitTrim($event)"
      :scrollToTrimLabel="scrollToTrimLabel">
    </vehicleTrimBot>
  </fieldset>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Provide,
  Vue,
  Watch,
  Inject,
  Ref
} from 'vue-property-decorator';

import {
  State, Getter, Action, Mutation, namespace
} from 'vuex-class';

import VeeValidate, { FieldError, Validator } from 'vee-validate';

import { trimChatbotService } from '@/services/TrimChatbotService';
import { tokenService } from '@/services/TokenService';
import VehicleLevel from '@/enums/VehicleLevel';
import TrimsOrderedBy from '@/enums/TrimsOrderedBy';
import IDerivative from '@/models/vehicle/interfaces/derivative.d';
import { validationService } from '../../services/ValidationService';
import SearchQuery from '../../models/SearchQuery';
import Touchpoint from '../../models/Touchpoint';
import GenericTouchPointComponent from '../ui/GenericTouchPointComponent.vue';
import GenericTouchPointTextComponent from '../ui/GenericTouchPointTextComponent.vue';
import ExtraData from '../../models/ExtraData';
import VehicleTrimBotComponent from './VehicleTrimBotComponent.vue';

const VehicleSearchModule = namespace('vehicleSearchModule');
const SearchQueryModule = namespace('searchQueryModule');

@Component({
  components: {
    touchpoint: GenericTouchPointComponent,
    touchpointText: GenericTouchPointTextComponent,
    vehicleTrimBot: VehicleTrimBotComponent
  }
})
export default class VehicleTrimComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @SearchQueryModule.Getter public searchQuery!: SearchQuery;

  @VehicleSearchModule.Getter public trims!: string[] | null;

  @VehicleSearchModule.Getter public derivatives!: IDerivative[];

  public trimOptions: Touchpoint[] = [];

  public trimNotSure: boolean = false;

  public trimSelected: string | null = null;

  @Prop({ default: 'Trim' })
  public trimLabel!: string;

  @Prop({ default: 'required' })
  public validationRules!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public allowNotSure!: boolean;

  @Prop({ default: false })
  public ignoreDerivative!: boolean;

  @Prop({ default: '' })
  public touchpointKeySuffix!: string;

  @Prop({ default: false })
  public allowTrimBot!: boolean;

  @Prop({ default: TrimsOrderedBy.Alphabetical })
  public trimsOrderedBy!: TrimsOrderedBy;

  @Ref()
  public vehicleTrimBot!: VehicleTrimBotComponent;

  @Ref('trimLabelElement')
  public readonly trimLabelElement!: HTMLLabelElement;

  public getTrimValidationClass(fieldType: string) {
    if ((this.trim !== null && this.trim.length > 0) || this.trimNotSure) {
      return '';
    }

    return this.getValidationClass('dq-vehicle-customisation', fieldType);
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  public get isSingleDerivative(): boolean {
    return this.derivatives !== null && this.derivatives.length === 1;
  }

  public get derivativesHaveNoTrims(): boolean {
    return this.derivatives !== null && this.derivatives.length > 0
      && (this.trims === null || this.trims.length === 0);
  }

  public get showTrims(): boolean {
    return this.trimOptions.length > 1;
  }

  public get bodystyle(): string {
    if (this.searchQuery.bodystyles && this.searchQuery.bodystyles.length > 0) {
      return this.searchQuery.bodystyles[0];
    }
    return '';
  }

  public get engineSize(): string {
    if (this.searchQuery.engineSize) {
      return `${this.searchQuery.engineSize}`;
    }

    const extraData: ExtraData = this.$store.getters['enquiryModule/extraData']
      .find((x: ExtraData) => x.keyName === 'engineSizeNotSure');

    if (extraData) {
      return 'transmissionNotSure';
    }

    return '';
  }

  public setTrimOptions() {
    this.trimNotSure = false;
    this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'trimNotSure');

    if (this.trims !== null && this.trims.length === 1) {
      // eslint-disable-next-line prefer-destructuring
      this.submitTrim(this.trims[0]);
      this.trimOptions = [];
    } else if (this.isSingleDerivative || this.derivativesHaveNoTrims) {
      this.submitTrim(null);
      this.trimOptions = [];
    } else if (this.trims !== null) {
      this.trimOptions = this.trims.map((trim: string) => new Touchpoint(trim, trim));

      switch (this.trimsOrderedBy) {
        case TrimsOrderedBy.Alphabetical:
          this.trimOptions.sort((a, b) => a.touchpointName.localeCompare(b.touchpointName));
          break;
        case TrimsOrderedBy.PriceAscending:
        case TrimsOrderedBy.PriceDescending:
          this.trimOptions.sort((a, b) => {
            const trimA = this.derivatives.find((d) => d.trimName === a.touchpointName);
            const trimB = this.derivatives.find((d) => d.trimName === b.touchpointName);
            if (trimA && trimB) {
              const priceDiff = (trimA.basicPrice || 0) - (trimB.basicPrice || 0);
              return this.trimsOrderedBy === TrimsOrderedBy.PriceAscending ? priceDiff : -priceDiff;
            }
            return 0;
          });
          break;
        default:
          this.trimOptions.sort((a, b) => a.touchpointName.localeCompare(b.touchpointName));
          break;
      }
    } else {
      this.trimOptions = [];
    }
  }

  public get trimText() {
    return tokenService.replaceToken(this.trimLabel);
  }

  public get trim(): string | null {
    return this.searchQuery.trims && this.searchQuery.trims.length > 0 ? this.searchQuery.trims[0] : null;
  }

  public get modelId(): number | null {
    return this.searchQuery.modelId !== null ? this.searchQuery.modelId : 0;
  }

  public async submitTrim(value: string | null) {
    if (value !== null) {
      this.trimNotSure = false;
      this.trimSelected = value;
      this.$store.dispatch('enquiryModule/submitRemoveExtraData', 'trimNotSure');

      await this.$store.dispatch('vehicleModule/submitTrims', [value]);
      await this.$store.dispatch('vehicleSearchModule/updateFilteredResults');

      if (!this.ignoreDerivative) {
        this.$store.dispatch('vehicleModule/submitDerivative', this.derivatives[0]);
      }
    } else {
      await this.$store.dispatch('vehicleModule/submitTrims', null);
      await this.$store.dispatch('vehicleSearchModule/updateFilteredResults');

      if (!this.ignoreDerivative) {
        this.$store.dispatch('vehicleModule/submitDerivative', this.derivatives[0]);
      }
    }

    this.$emit('trimsSet');

    this.$validator.validateAll(this.validationScope);
  }

  public async submitExtraData() {
    this.trimNotSure = true;

    if (this.allowTrimBot && this.vehicleTrimBot) {
      trimChatbotService.warmUp();
      this.vehicleTrimBot.openOrCloseBot(true);
    }
    const extraData: ExtraData = new ExtraData('trimNotSure', 'true');
    this.$store.dispatch('enquiryModule/submitExtraData', extraData);

    await this.$store.dispatch('vehicleModule/submitTrims', null);
    this.$store.dispatch('vehicleSearchModule/updateFilteredResults');

    if (!this.ignoreDerivative) {
      this.$store.dispatch('vehicleModule/submitDerivative', this.derivatives[0]);
    }

    this.$emit('trimsSet');
  }

  public created() {
    this.setTrimOptions();
  }

  public scrollToTrimLabel(): void {
    const element = this.trimLabelElement;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  public get touchpointKey(): string {
    if (this.touchpointKeySuffix.length > 0) {
      return `${this.bodystyle}-${this.engineSize}-${this.modelId}`;
    }

    return `${this.bodystyle}-${this.modelId}`;
  }
}
</script>
